import { useRef } from "react";
import { useLocation, useOutlet } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Navbar } from "./Navbar";

const pageVariants = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

const pageTransition = {
  duration: 0.28,
  ease: [0.16, 1, 0.3, 1] as const,
};

/**
 * Captures the outlet element at the time of mount so that
 * AnimatePresence can hold the OLD page during exit animation
 * while useOutlet() already returns the NEW page.
 *
 * The key trick: only update the ref when React actually
 * re-creates this component (via the parent's `key` prop).
 */
function FrozenOutlet() {
  const outlet = useOutlet();
  const outletRef = useRef(outlet);
  // On first render of this instance, capture the outlet.
  // Because the parent motion.div is keyed by pathname,
  // a new FrozenOutlet instance is created per route.
  return <>{outletRef.current}</>;
}

export function AppLayout() {
  const location = useLocation();
  const isPipelineBoardPage = location.pathname === "/pipeline";

  return (
    <div className="min-h-screen bg-[var(--color-bg-primary)]">
      <div className="animate-slide-in-down">
        <Navbar />
      </div>
      <main className={isPipelineBoardPage ? "w-full px-0 py-0" : "mx-auto max-w-7xl px-6 py-8"}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={location.pathname}
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={pageTransition}
          >
            <FrozenOutlet />
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}
